import { createEvent, Effect } from 'effector';
import { RequestStrategyEnum } from '@shared/api';

const API_DELAY = 300;

//**In case of abort strategy usage also used abortApiFailCaseTrigger if fail case of effect to prevent error events or loader blinks*/
//**In case of abort strategy usage to prevent error messages from store also use errorStoreAbortRequestWatcher in $errorStore.watch */

export class ApiUtilsV2 {
  static wrapperApi = <Req, Res>(effect: Effect<Req, Res, Error>, strategy: RequestStrategyEnum = RequestStrategyEnum.Default) => {
    const triggerEvent = createEvent<Req>();
    const cancelEvent = createEvent<void>();
    let timeoutId: NodeJS.Timeout | null = null;
    const uniqueRequests = new Set<string>();
    const uniqueRequestsList: Req[] = [];
    let abortController: AbortController | null = null;

    triggerEvent.watch(async (request) => {
      const requestKey = JSON.stringify(request);
      const requestObject = request;

      switch (strategy) {
        case RequestStrategyEnum.Default: {
          if (!uniqueRequests.has(requestKey)) {
            uniqueRequests.add(requestKey);
            const result = effect(requestObject);
            if (result && typeof result.finally === 'function') result.finally(() => uniqueRequests.delete(requestKey));
            else uniqueRequests.delete(requestKey);
          }
          break;
        }
        case RequestStrategyEnum.Debounce: {
          const requestKey = JSON.stringify(request);

          if (!uniqueRequests.has(requestKey)) {
            uniqueRequests.add(requestKey);
            uniqueRequestsList.push(request);

            if (timeoutId) clearTimeout(timeoutId);

            timeoutId = setTimeout(() => {
              uniqueRequestsList.forEach((req) => {
                const result = effect(req);
                if (result && typeof result.finally === 'function') result.finally(() => uniqueRequests.delete(requestKey));
                else uniqueRequests.delete(requestKey);
              });
              uniqueRequestsList.length = 0;
            }, API_DELAY);
          }
          break;
        }
        case RequestStrategyEnum.Abort: {
          if (abortController) abortController.abort();
          abortController = new AbortController();
          const result = effect({ ...request, signal: abortController.signal });
          if (result && typeof result.finally === 'function') result.finally(() => uniqueRequests.delete(requestKey));
          else uniqueRequests.delete(requestKey);
          break;
        }
      }
    });

    cancelEvent.watch(() => {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    });

    return { trigger: triggerEvent, cancel: cancelEvent };
  };

  static isAbortError = (error: Error) => error.name === 'AbortError';

  static abortApiLoadingFailCaseTrigger = (error: Error) => ApiUtilsV2.isAbortError(error);

  static abortApiErrorFailCaseTrigger = (error: Error, message?: string) => {
    if (ApiUtilsV2.isAbortError(error)) return '';
    return message ?? error.message;
  };
}
